import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {Box, Button, IconButton, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import {LocalizedText} from "crqlar-react-common/dist/models/common/localizedText.model";
import {SurveyQuestionOption} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import React, {useEffect, useRef} from "react";
import {useTranslation} from "react-i18next";
import {insertOption} from "../../shared/surveyQuestions.util";
import {getSurveyQuestionTypeFacts} from "../../shared/surveyQuestionTypeFacts";
import {createEditorOption} from "../surveyEditorQuestions.util";
import {InlineInput, InlineLabelledField, QuestionEditorProps} from "./questionEditor.types";

const writeLocalized = (
    text: LocalizedText | undefined,
    language: string,
    value: string
): LocalizedText | undefined => {
    const next: LocalizedText = {...text};

    if (value.trim()) {
        next[language] = value;
    } else {
        delete next[language];
    }

    return Object.keys(next).length ? next : undefined;
};

function ChoiceQuestionEditor(props: QuestionEditorProps) {
    const {t} = useTranslation();
    const {question, patch, language, disabled} = props;

    const showOther = !!question.allowOther && !!getSurveyQuestionTypeFacts(question.type)?.otherOption;

    /*
        ADD OPTION left the caret in the option above it, so the next thing typed was appended to
        the previous label and the new row stayed empty. The row does not exist yet when the button
        is pressed - the parent re-renders with it - so the id is parked here and claimed by the
        effect once that row's input has mounted.
    */
    const pendingFocusRef = useRef<string | undefined>(undefined);
    const optionInputsRef = useRef<Record<string, HTMLInputElement | null>>({});

    useEffect(() => {
        const optionId = pendingFocusRef.current;
        if (!optionId) return;

        const input = optionInputsRef.current[optionId];
        if (!input) return;

        pendingFocusRef.current = undefined;
        input.focus();
    });

    const addOption = () => {
        const option = createEditorOption();
        pendingFocusRef.current = option.id;

        patch({options: insertOption(question.options ?? [], option)});
    };

    const patchOption = (optionId: string, partial: Partial<SurveyQuestionOption>) => patch({
        options: (question.options ?? []).map(option => (
            option.id === optionId ? {...option, ...partial} : option
        )),
    });

    return (
        <Stack gap={1.5}>
            <InlineLabelledField label={t('Options')}>
                <Stack gap={1} sx={{width: '100%'}}>
                    {(question.options ?? []).map(option => (
                        <Stack key={option.id} direction={'row'} alignItems={'center'} gap={1}>
                            <Box sx={{flex: 1, minWidth: 0}}>
                                <InlineInput
                                    value={option.label?.[language] ?? ''}
                                    inputRef={(input: HTMLInputElement | null) => {
                                        if (option.id) optionInputsRef.current[option.id] = input;
                                    }}
                                    onChange={(value) => patchOption(option.id!, {
                                        label: {...option.label, [language]: value},
                                    })}
                                    disabled={disabled}
                                />
                            </Box>

                            <IconButton
                                size={'small'}
                                disabled={disabled}
                                onClick={() => patch({
                                    options: (question.options ?? []).filter(entry => entry.id !== option.id)
                                })}
                            >
                                <DeleteOutlineIcon fontSize={'small'}/>
                            </IconButton>
                        </Stack>
                    ))}

                    {showOther && (
                        <Stack
                            gap={0.5}
                            sx={{
                                p: 1,
                                borderRadius: 1,
                                border: theme => `1px dashed ${theme.palette.divider}`,
                            }}
                        >
                            <Stack direction={'row'} alignItems={'center'} gap={1} sx={{minWidth: 0}}>
                                <Typography variant={'body2'} sx={{flex: '0 0 auto'}}>
                                    {t('Other')}
                                </Typography>

                                <Box sx={{flex: 1, minWidth: 0}}>
                                    <InlineInput
                                        value={question.otherPlaceholder?.[language] ?? ''}
                                        onChange={(value) => patch({
                                            otherPlaceholder: writeLocalized(
                                                question.otherPlaceholder,
                                                language,
                                                value
                                            ),
                                        })}
                                        placeholder={t('Please specify')}
                                        disabled={disabled}
                                    />
                                </Box>
                            </Stack>

                            <Typography variant={'caption'} color={'text.secondary'}>
                                {t('Guests type their own answer here. This text is the placeholder they see.')}
                            </Typography>
                        </Stack>
                    )}

                    <Button
                        size={'small'}
                        startIcon={<AddIcon/>}
                        disabled={disabled}
                        sx={{alignSelf: 'flex-start'}}
                        onClick={addOption}
                    >
                        {t('Add option')}
                    </Button>
                </Stack>
            </InlineLabelledField>
        </Stack>
    );
}

export default ChoiceQuestionEditor;
